<template>
  <div class="space-y-6 sm:space-y-8">
    <div
      v-for="(group, groupIdx) in groups"
      :key="groupKey(group)"
      class="card-fbochas overflow-hidden"
    >
      <button
        type="button"
        :aria-expanded="groupIdx === 0 || !isCollapsed(groupKey(group))"
        :class="[
          'flex w-full items-center justify-between gap-3 px-5 text-left transition-colors sm:p-5 md:px-8',
          isCollapsed(groupKey(group))
            ? 'py-5 hover:bg-white/5 sm:py-8'
            : 'pt-5 pb-10 sm:pt-8 sm:pb-10',
        ]"
        @click="groupIdx === 0 ? null : toggleGroup(groupKey(group))"
      >
        <div class="flex min-w-0 flex-1 items-start gap-3 sm:items-center">
          <IconFbochas name="calendarDays" :size="40" />
          <div class="min-w-0 flex-1 sm:flex sm:items-center sm:gap-3">
            <span
              class="block text-lg font-semibold leading-tight text-white sm:truncate sm:text-3xl"
            >
              {{ weekendLabel(group) }}
            </span>
            <span
              class="mt-2 inline-flex flex-shrink-0 rounded-3xl bg-bochas-card-border px-3 py-1.5 text-xs font-semibold text-white sm:mt-0 sm:px-6 sm:py-4 sm:text-base"
            >
              {{ pluralizeTorneos(group.items.length) }}
            </span>
          </div>
        </div>
        <IconFbochas
          v-if="groupIdx > 0"
          :name="isCollapsed(groupKey(group)) ? 'chevron-down' : 'chevron-up'"
          :size="22"
          class="mt-12 flex-shrink-0 text-gray-300 sm:mt-0"
        />
      </button>

      <Transition name="resultados-collapse">
        <div
          v-if="groupIdx === 0 || !isCollapsed(groupKey(group))"
          class="resultados-day-scroll resultados-title-divider"
        >
          <div
            v-for="(torneo, idx) in group.items"
            :key="torneo.id"
            :class="idx > 0 ? 'resultados-row-divider' : ''"
          >
            <button
              type="button"
              class="relative flex w-full items-center justify-between gap-3 px-5 py-[25px] text-left transition-colors hover:bg-white/5 sm:px-6 md:px-6"
              :aria-expanded="
                !isTournamentCollapsed(torneoKey(groupKey(group), torneo))
              "
              @click="toggleTournament(torneoKey(groupKey(group), torneo))"
            >
              <div class="min-w-0 flex-1">
                <p
                  class="text-lg font-medium leading-snug text-white sm:truncate sm:text-2xl"
                >
                  <span>{{ torneo.name }}</span>
                  <span v-if="dateRangeLabel(torneo)" class="block sm:inline">
                    <span class="hidden sm:inline"> - </span>
                    {{ dateRangeLabel(torneo) }}
                  </span>
                </p>
                <p
                  class="mt-1 flex flex-wrap items-center font-normal gap-2 text-sm text-white sm:text-sm"
                >
                  <span v-if="torneo.location">{{ torneo.location }}</span>
                  <span v-if="torneo.location && torneo.association">·</span>
                  <span v-if="torneo.association">{{
                    torneo.association
                  }}</span>
                  <span
                    v-if="
                      (torneo.location || torneo.association) &&
                      (torneo.categories?.length || 0)
                    "
                    >·</span
                  >
                  <span>{{
                    pluralizeCategorias(torneo.categories?.length || 0)
                  }}</span>
                </p>
              </div>
              <IconFbochas
                :name="
                  isTournamentCollapsed(torneoKey(groupKey(group), torneo))
                    ? 'chevron-down'
                    : 'chevron-up'
                "
                :size="20"
                class="absolute right-5 top-[4.65rem] text-gray-300 sm:static sm:flex-shrink-0"
              />
            </button>

            <Transition name="resultados-collapse">
              <ResultadoCard
                v-if="
                  !isTournamentCollapsed(torneoKey(groupKey(group), torneo))
                "
                :torneo="torneo"
                :index="idx"
                variant="home"
                :show-header="false"
                :show-title="false"
                class="!rounded-none !border-0 !bg-transparent !pt-0 !backdrop-blur-none"
                @open-gallery="$emit('open-gallery', $event)"
              />
            </Transition>
          </div>
        </div>
      </Transition>
    </div>
  </div>
</template>

<script setup>
import { ref, watch } from "vue";
import { formatDate } from "@/utils/date";
import IconFbochas from "@/components/Icons/IconFbochas.vue";
import ResultadoCard from "../Resultados/ResultadoCard.vue";

const props = defineProps({
  groups: { type: Array, required: true },
});

defineEmits(["open-gallery"]);

const collapsed = ref(new Set());
const collapsedTournaments = ref(new Set());

const MONTHS_ES = [
  "enero",
  "febrero",
  "marzo",
  "abril",
  "mayo",
  "junio",
  "julio",
  "agosto",
  "septiembre",
  "octubre",
  "noviembre",
  "diciembre",
];

const MONTHS_TITLE_ES = [
  "Enero",
  "Febrero",
  "Marzo",
  "Abril",
  "Mayo",
  "Junio",
  "Julio",
  "Agosto",
  "Septiembre",
  "Octubre",
  "Noviembre",
  "Diciembre",
];

function isCollapsed(date) {
  return collapsed.value.has(date);
}

function toggleGroup(date) {
  const next = new Set(collapsed.value);
  next.has(date) ? next.delete(date) : next.add(date);
  collapsed.value = next;
}

function groupKey(group) {
  return group.label || group.date;
}

function torneoKey(date, torneo) {
  return `${date}-${torneo.id}`;
}

function isTournamentCollapsed(key) {
  return collapsedTournaments.value.has(key);
}

function toggleTournament(key) {
  const next = new Set(collapsedTournaments.value);
  next.has(key) ? next.delete(key) : next.add(key);
  collapsedTournaments.value = next;
}

function pluralizeTorneos(count) {
  if (count === 0) return "Sin torneos";
  if (count === 1) return "Un Torneo";
  return `${count} Torneos`;
}

function pluralizeCategorias(count) {
  if (count === 0) return "Sin categorías";
  if (count === 1) return "Una categoría";
  return `${count} categorías`;
}

function parseLocalDate(value) {
  const [y, m, d] = String(value || "")
    .split("T")[0]
    .split("-")
    .map((n) => parseInt(n, 10));
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
}

function dateRangeLabel(torneo) {
  if (torneo.date_range) return torneo.date_range;
  const values = (torneo.dates || [])
    .map((date) => date.event_date || date)
    .filter(Boolean)
    .sort();
  if (!values.length) return formatDate(torneo.event_date);

  const parsed = values
    .map((value) => {
      const [y, m, d] = String(value)
        .split("T")[0]
        .split("-")
        .map((n) => parseInt(n, 10));
      if (!y || !m || !d) return null;
      return { y, m, d };
    })
    .filter(Boolean);
  if (!parsed.length) return formatDate(torneo.event_date);

  const first = parsed[0];
  const sameMonth = parsed.every(
    (item) => item.y === first.y && item.m === first.m,
  );
  if (!sameMonth) {
    return parsed
      .map((item) => `${item.d} de ${MONTHS_ES[item.m - 1]} ${item.y}`)
      .join(", ");
  }

  const days = parsed.map((item) => item.d);
  const daysLabel =
    days.length === 1
      ? `${days[0]}`
      : `${days.slice(0, -1).join(", ")} y ${days[days.length - 1]}`;
  return `${daysLabel} de ${MONTHS_ES[first.m - 1]} ${first.y}`;
}

function weekendLabel(group) {
  const firstItem = group.items?.[0] || {};
  const dateValues = (firstItem.dates || [])
    .map((date) => date.event_date || date)
    .filter(Boolean)
    .sort();
  const source = dateValues[0] || group.date;
  const date = parseLocalDate(source);
  if (!date) return group.label || formatDate(group.date);

  const dow = date.getDay();
  const weekendStart =
    dow === 5
      ? date
      : new Date(
          date.getFullYear(),
          date.getMonth(),
          date.getDate() - ((dow + 2) % 7),
        );
  const y = weekendStart.getFullYear();
  const m = weekendStart.getMonth();
  const firstOfMonth = new Date(y, m, 1);
  const offsetToFirstFriday = (5 - firstOfMonth.getDay() + 7) % 7;
  const firstFriday = new Date(y, m, 1 + offsetToFirstFriday);
  const diffDays = Math.round(
    (weekendStart.getTime() - firstFriday.getTime()) / 86400000,
  );
  const nth = Math.floor(diffDays / 7) + 1;
  const ordinal =
    nth === 1
      ? "Primer"
      : nth === 2
      ? "Segundo"
      : nth === 3
      ? "Tercer"
      : nth === 4
      ? "Cuarto"
      : "Quinto";
  return `${ordinal} fin de semana ${MONTHS_TITLE_ES[m]} ${y}`;
}

function tournamentTitle(torneo) {
  const range = dateRangeLabel(torneo);
  return range ? `${torneo.name} - ${range}` : torneo.name;
}

watch(
  () => props.groups,
  (groups) => {
    collapsed.value = new Set(groups.slice(1).map((g) => groupKey(g)));
    collapsedTournaments.value = new Set(
      groups.flatMap((group) =>
        group.items
          .map((torneo) => torneoKey(groupKey(group), torneo)),
      ),
    );
  },
  { immediate: true, deep: false },
);
</script>

<style scoped>
.resultados-collapse-enter-active,
.resultados-collapse-leave-active {
  overflow: hidden;
  transition: max-height 0.4s ease-in-out, opacity 0.25s ease-in-out;
}

.resultados-collapse-enter-from,
.resultados-collapse-leave-to {
  max-height: 0;
  opacity: 0;
}

.resultados-collapse-enter-to,
.resultados-collapse-leave-from {
  max-height: 4000px;
  opacity: 1;
}

@media (prefers-reduced-motion: reduce) {
  .resultados-collapse-enter-active,
  .resultados-collapse-leave-active {
    transition: none;
  }
}

.resultados-day-scroll {
  scrollbar-width: thin;
  scrollbar-color: rgba(255, 255, 255, 0.24) transparent;
}

.resultados-day-scroll::-webkit-scrollbar {
  width: 6px;
}

.resultados-day-scroll::-webkit-scrollbar-track {
  background: transparent;
}

.resultados-day-scroll::-webkit-scrollbar-thumb {
  background: rgba(255, 255, 255, 0.22);
  border-radius: 999px;
}

.resultados-day-scroll::-webkit-scrollbar-thumb:hover {
  background: rgba(255, 255, 255, 0.36);
}

.resultados-title-divider {
  position: relative;
}

.resultados-title-divider::before,
.resultados-row-divider {
  position: relative;
}

.resultados-title-divider::before,
.resultados-row-divider::before {
  content: "";
  position: absolute;
  top: 0;
  left: 1.25rem;
  right: 1.25rem;
  height: 1px;
  background: rgba(255, 255, 255, 0.16);
}

@media (min-width: 640px) {
  .resultados-title-divider::before,
  .resultados-row-divider::before {
    left: 1.5rem;
    right: 1.5rem;
  }
}

@media (min-width: 768px) {
  .resultados-title-divider::before,
  .resultados-row-divider::before {
    left: 2rem;
    right: 2rem;
  }
}
</style>
